/**
 * Invoicing page model — pure functions: invoice rows with their per-milestone split, the
 * received / awaiting / not-started summary, the contract "money bar", the period / project / client /
 * system / milestone filters, the data check (scheduled milestones vs. contract value) and the export.
 *
 * Status meaning (unchanged from the page before): "Payment Received" = received; any other raised
 * status (Submitted …) = awaiting payment; "Not Started" = a scheduled milestone not invoiced yet.
 */
import { toDateInputValue, toNumber } from '@/lib/format';

const num = (value) => toNumber(value) ?? 0;
const text = (value) => String(value ?? '').trim();

export const MILESTONES = [
  { key: 'Advanced payment', label: 'Advanced Payment', matches: ['Advanced payment'] },
  { key: 'Against Delivery', label: 'Material Delivery', matches: ['Against Delivery'] },
  { key: 'Cable pulling', label: 'Cable Pulling', matches: ['Cable pulling'] },
  { key: 'Installation', label: 'Installation', matches: ['Installation'] },
  { key: 'T&C', label: 'T&C', matches: ['T&C'] },
  { key: 'Handing Over', label: 'Handing Over', matches: ['Handing Over'] },
  { key: 'Retention', label: 'Retention', matches: ['Retention'] },
];

export const parseMultiSelectString = (value) => (
  [...new Set(String(value || '').split(',').map((item) => item.trim()).filter(Boolean))]
);

const normalizePercentages = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.entries(value).reduce((acc, [key, raw]) => {
    const name = String(key || '').trim();
    const parsed = Number(raw);
    if (!name || !Number.isFinite(parsed) || parsed < 0) return acc;
    acc[name] = parsed;
    return acc;
  }, {});
};

/** How an invoice's amount splits over its milestone types (equal shares unless valid percentages). */
export function allocateAmount(amount, invoiceTypes, rawPercentages) {
  const types = parseMultiSelectString(invoiceTypes);
  const total = Number(amount || 0);
  if (!types.length) return {};
  if (types.length === 1) return { [types[0]]: total };
  const percentages = normalizePercentages(rawPercentages);
  const hasAll = types.every((type) => Object.prototype.hasOwnProperty.call(percentages, type));
  const sum = types.reduce((acc, type) => acc + Number(percentages[type] || 0), 0);
  if (!(hasAll && Math.abs(sum - 100) <= 0.01)) {
    const share = total / types.length;
    return types.reduce((acc, type) => ({ ...acc, [type]: share }), {});
  }
  return types.reduce((acc, type) => ({ ...acc, [type]: (total * Number(percentages[type] || 0)) / 100 }), {});
}

export const systemOf = (project) => text(project?.system_type) || 'Unassigned';

/** Invoices joined with their project: amount, status bucket, types and the per-type split. */
export function buildInvoiceRows(invoices, projectMap) {
  return (Array.isArray(invoices) ? invoices : []).map((invoice) => {
    const project = projectMap[invoice.project_id] || {};
    const amount = num(invoice.amount);
    const types = parseMultiSelectString(invoice.invoice_type);
    const status = text(invoice.status) || 'Not Started';
    return {
      id: invoice.id,
      invoice,
      projectId: invoice.project_id,
      projectName: text(project.name),
      client: text(project.client),
      systemType: systemOf(project),
      reference: text(invoice.reference),
      note: text(invoice.note),
      createdDay: toDateInputValue(invoice.created_at),
      amount,
      status,
      bucket: status === 'Payment Received' ? 'received' : status === 'Not Started' ? 'notStarted' : 'awaiting',
      types,
      typeAmountMap: allocateAmount(amount, types, invoice.invoice_type_percentages),
      inQuickBooks: Boolean(invoice.quickbooks?.invoice_id),
    };
  });
}

/** Received / awaiting / not started totals and the per-milestone breakdown. */
export function summarizeRows(rows) {
  const sumBy = (bucket) => rows.filter((row) => row.bucket === bucket).reduce((sum, row) => sum + row.amount, 0);
  const received = sumBy('received');
  const awaiting = sumBy('awaiting');
  const notStarted = sumBy('notStarted');
  const byMilestone = MILESTONES.map((milestone) => {
    const share = (row) => row.types.reduce((sum, type) => (milestone.matches.includes(type) ? sum + num(row.typeAmountMap[type]) : sum), 0);
    const matching = rows.filter((row) => row.types.some((type) => milestone.matches.includes(type)));
    const by = (bucket) => matching.filter((row) => row.bucket === bucket).reduce((sum, row) => sum + share(row), 0);
    const milestoneReceived = by('received');
    const milestoneAwaiting = by('awaiting');
    const milestoneNotStarted = by('notStarted');
    return {
      ...milestone,
      count: matching.length,
      received: milestoneReceived,
      awaiting: milestoneAwaiting,
      notStarted: milestoneNotStarted,
      invoiced: milestoneReceived + milestoneAwaiting,
      total: milestoneReceived + milestoneAwaiting + milestoneNotStarted,
    };
  });
  return {
    received,
    awaiting,
    notStarted,
    invoiced: received + awaiting,
    scheduled: received + awaiting + notStarted,
    invoicedCount: rows.filter((row) => row.bucket !== 'notStarted').length,
    awaitingCount: rows.filter((row) => row.bucket === 'awaiting').length,
    awaitingProjects: new Set(rows.filter((row) => row.bucket === 'awaiting').map((row) => row.projectId)).size,
    notInQuickBooks: rows.filter((row) => row.bucket !== 'notStarted' && !row.inQuickBooks).length,
    retentionNotStarted: byMilestone.find((milestone) => milestone.key === 'Retention')?.notStarted || 0,
    byMilestone,
  };
}

/**
 * The money bar: base (contract value, or the milestone's scheduled total when one milestone is picked)
 * split into received / awaiting / the rest. Shares are % of the base; "of invoiced" = received ÷ invoiced.
 */
export function moneyBar(summary, base) {
  const safeBase = Math.max(base, summary.received + summary.awaiting, 0);
  const rest = Math.max(0, safeBase - summary.received - summary.awaiting);
  const pct = (value) => (safeBase > 0 ? (value / safeBase) * 100 : 0);
  return {
    base: safeBase,
    received: summary.received,
    awaiting: summary.awaiting,
    rest,
    receivedPct: pct(summary.received),
    awaitingPct: pct(summary.awaiting),
    restPct: pct(rest),
    collectedOfInvoicedPct: summary.invoiced > 0 ? (summary.received / summary.invoiced) * 100 : 0,
  };
}

/* ------------------------------------------------------------------ what the bar measures */

/**
 * What the money bar — and each system row — measures in the current view, so a filtered amount is
 * never read against the whole contract:
 *  - 'contract'  only scope filters (project / client / system): the contract value; rest = not invoiced yet.
 *  - 'milestone' a milestone is picked: that milestone's scheduled total; rest = not started.
 *  - 'search'    a search is typed: the scheduled total of the matching invoices; rest = not started.
 *  - 'period'    a period is picked: what was invoiced among that period's records (received + awaiting);
 *                there is no "rest" — milestones recorded in the period but not invoiced are a KPI.
 * A period wins over a search, a search over a milestone. Labels name every filter that shaped the number.
 */
export function viewBasis({ period, milestone, search, status, scopeLabel = 'all projects' } = {}) {
  const milestoneDef = milestone ? MILESTONES.find((item) => item.key === milestone) || null : null;
  const needle = text(search);
  const statusText = text(status);
  const matching = [
    statusText ? `status ${statusText}` : '',
    needle ? `matching “${needle.length > 24 ? `${needle.slice(0, 23)}…` : needle}”` : '',
  ].filter(Boolean).join(' · ');
  const join = (parts) => parts.filter(Boolean).join(' · ');
  if (period?.from) {
    return {
      mode: 'period',
      label: join([`Invoiced · recorded in ${period.label}`, milestoneDef?.label, matching, scopeLabel]),
      restLabel: null,
      ofBase: `of invoices recorded in ${period.label}`,
      rowNoun: 'invoiced',
    };
  }
  if (needle || statusText) {
    return {
      mode: 'search',
      label: join(['Scheduled', milestoneDef?.label, matching, scopeLabel]),
      restLabel: 'Not started',
      ofBase: 'of the matching milestones',
      rowNoun: 'scheduled',
    };
  }
  if (milestoneDef) {
    return {
      mode: 'milestone',
      label: join(['Scheduled', milestoneDef.label, scopeLabel]),
      restLabel: 'Not started',
      ofBase: `of the ${milestoneDef.label} schedule`,
      rowNoun: 'scheduled',
    };
  }
  return {
    mode: 'contract',
    label: join(['Contract value', scopeLabel]),
    restLabel: 'Not invoiced yet',
    ofBase: 'of contract value',
    rowNoun: 'contract',
  };
}

/** The amount the bar splits for a basis: contract value, invoiced (period) or scheduled (milestone / search). */
export function basisAmount(mode, { contract = 0, summary }) {
  if (mode === 'contract') return contract;
  if (mode === 'period') return summary.invoiced;
  return summary.scheduled;
}

/* ------------------------------------------------------------------ periods */

const pad2 = (value) => String(value).padStart(2, '0');

/** Period options relative to today (GST): This year, this quarter, last quarter, All time. */
export function periodOptions(now = new Date()) {
  const today = toDateInputValue(now) || `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  const quarter = Math.ceil(month / 3);
  // Day 0 of the next month = the quarter's real last day (30 Jun, 30 Sep, 31 Dec, 31 Mar).
  const lastDay = (y, q) => new Date(Date.UTC(y, q * 3, 0)).getUTCDate();
  const quarterRange = (y, q) => ({ from: `${y}-${pad2((q - 1) * 3 + 1)}-01`, to: `${y}-${pad2(q * 3)}-${pad2(lastDay(y, q))}` });
  const previous = quarter === 1 ? { y: year - 1, q: 4 } : { y: year, q: quarter - 1 };
  return [
    { key: 'year', label: 'This year', from: `${year}-01-01`, to: `${year}-12-31` },
    { key: 'quarter', label: `Q${quarter} ${year}`, ...quarterRange(year, quarter) },
    { key: 'last-quarter', label: `Q${previous.q} ${previous.y}`, ...quarterRange(previous.y, previous.q) },
    { key: 'all', label: 'All time', from: '', to: '' },
  ];
}

/** Rows inside the period (by the day the invoice / milestone was recorded, GST). */
export function inPeriod(row, period) {
  if (!period || !period.from) return true;
  return Boolean(row.createdDay) && row.createdDay >= period.from && row.createdDay <= period.to;
}

/**
 * Applies the page filters. A milestone filter keeps only that milestone's share of each invoice,
 * so a combined "T&C, Handing Over" invoice counts half (or its % split) under T&C.
 */
export function filterRows(rows, { period, projectId, client, system, milestone, search, status } = {}) {
  const needle = text(search).toLowerCase();
  const statusWanted = text(status).toLowerCase();
  const milestoneDef = milestone ? MILESTONES.find((item) => item.key === milestone) : null;
  return rows
    .filter((row) => {
      if (!inPeriod(row, period)) return false;
      if (projectId && row.projectId !== projectId) return false;
      if (client && row.client !== client) return false;
      if (system && row.systemType !== system) return false;
      if (statusWanted && row.status.toLowerCase() !== statusWanted) return false;
      if (milestoneDef && !row.types.some((type) => milestoneDef.matches.includes(type))) return false;
      if (!needle) return true;
      return [row.reference, row.projectName, row.client, row.note].some((value) => value.toLowerCase().includes(needle));
    })
    .map((row) => {
      if (!milestoneDef) return row;
      const share = row.types.reduce((sum, type) => (milestoneDef.matches.includes(type) ? sum + num(row.typeAmountMap[type]) : sum), 0);
      const types = row.types.filter((type) => milestoneDef.matches.includes(type));
      return { ...row, amount: share, types, typeAmountMap: Object.fromEntries(types.map((type) => [type, num(row.typeAmountMap[type])])) };
    });
}

/** Projects in scope of the project / client / system filters (contract value base). */
export function scopeProjects(projects, { projectId, client, system } = {}) {
  return (Array.isArray(projects) ? projects : []).filter((project) => {
    if (projectId && project.id !== projectId) return false;
    if (client && text(project.client) !== client) return false;
    if (system && systemOf(project) !== system) return false;
    return true;
  });
}

export const contractValue = (projects) => projects.reduce((sum, project) => sum + num(project.total_project_value), 0);

/* ------------------------------------------------------------------ data check */

/**
 * Do the numbers close? Scheduled milestones (Not Started) vs. the contract value not invoiced yet,
 * and every project whose milestones (invoiced + scheduled) add up to more than its contract value.
 * Differences of 1 AED or less are rounding and ignored.
 */
export function dataCheck(rows, projects) {
  const summary = summarizeRows(rows);
  const contract = contractValue(projects);
  const uninvoiced = contract - summary.invoiced;
  const scheduledExcess = summary.notStarted - uninvoiced;
  const byProject = new Map();
  rows.forEach((row) => byProject.set(row.projectId, (byProject.get(row.projectId) || 0) + row.amount));
  const overScheduled = projects
    .map((project) => {
      const scheduled = byProject.get(project.id) || 0;
      const value = num(project.total_project_value);
      return { id: project.id, name: text(project.name) || 'Unnamed project', system: systemOf(project), contract: value, scheduled, excess: scheduled - value };
    })
    .filter((project) => project.scheduled > 0 && project.excess > 1)
    .sort((a, b) => b.excess - a.excess);
  return {
    notStarted: summary.notStarted,
    uninvoiced,
    scheduledExcess,
    hasExcess: scheduledExcess > 1,
    overScheduled,
    ok: !(scheduledExcess > 1) && overScheduled.length === 0,
  };
}

/* ------------------------------------------------------------------ export */

const milestoneName = (type) => MILESTONES.find((item) => item.matches.includes(type))?.label || type;

/**
 * "Ready to bill" milestones (GET /invoicing/ready-to-bill items) under the page filters: project, client, system
 * (from the project, as the register rows), milestone (raw invoice types), status (they are all "Not Started") and the
 * search. The period is not applied — ready means billable today. projectMap: { [id]: project }.
 */
export function filterReadyItems(items, { projectId, client, system, milestone, status, search } = {}, projectMap = {}) {
  const needle = text(search).toLowerCase();
  const statusWanted = text(status).toLowerCase();
  const milestoneDef = milestone ? MILESTONES.find((item) => item.key === milestone) : null;
  return (Array.isArray(items) ? items : []).filter((item) => {
    const project = projectMap[item.project_id] || null;
    if (projectId && item.project_id !== projectId) return false;
    const itemClient = text(project ? project.client : item.client);
    if (client && itemClient !== client) return false;
    const itemSystem = project ? systemOf(project) : (text(item.system_type) || 'Unassigned');
    if (system && itemSystem !== system) return false;
    if (statusWanted && statusWanted !== 'not started') return false;
    const types = Array.isArray(item.milestones) && item.milestones.length ? item.milestones : parseMultiSelectString(item.invoice_type);
    if (milestoneDef && !types.some((type) => milestoneDef.matches.includes(type))) return false;
    if (!needle) return true;
    return [item.reference, item.project_name, itemClient].some((value) => text(value).toLowerCase().includes(needle));
  });
}

export const INVOICE_EXPORT_COLUMNS = [
  { key: 'reference', label: 'Invoice no.' },
  { key: 'kind', label: 'Type', value: (row) => (row.kind === 'vo' ? 'Variation' : 'Base') },
  { key: 'projectName', label: 'Project' },
  { key: 'client', label: 'Client' },
  { key: 'systemType', label: 'System' },
  // The names the page shows ("Material Delivery", "Cable Pulling + Installation + T&C"), and the split.
  { key: 'milestone', label: 'Milestone', value: (row) => row.milestone || row.types.map(milestoneName).join(' + ') },
  { key: 'split', label: 'Split %', value: (row) => row.split || '' },
  { key: 'status', label: 'Status' },
  { key: 'amount', label: 'Amount (AED)', value: (row) => Math.round(row.amount * 100) / 100 },
  { key: 'createdDay', label: 'Recorded on' },
  { key: 'submittedOn', label: 'Submitted on', value: (row) => toDateInputValue(row.submittedOn) || '' },
  { key: 'ageDays', label: 'Days awaiting payment', value: (row) => (row.bucket === 'awaiting' && row.ageDays !== null && row.ageDays !== undefined ? row.ageDays : '') },
  { key: 'quickbooks', label: 'QuickBooks no.', value: (row) => row.invoice.quickbooks?.doc_number || '' },
  { key: 'note', label: 'Note' },
];
